import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  AlertCircle,
  BarChart3,
  Building2,
  Calendar,
  Car,
  ChevronLeft,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BeloLogo } from "@/components/BeloLogo";
import { Toaster } from "@/components/ui/sonner";

const NAV_ITEMS = [
  { to: "/dealer", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/dealer/inventory", label: "Inventory", icon: Car },
  { to: "/dealer/leads", label: "Leads", icon: Users },
  { to: "/dealer/appointments", label: "Appointments", icon: Calendar },
  { to: "/dealer/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dealer/billing", label: "Billing", icon: CreditCard, testId: "dealer-nav-billing" },
  { to: "/dealer/profile", label: "Dealership", icon: Building2, testId: "dealer-nav-profile" },
];

const NavItem = ({ item, onNavigate }) => (
  <NavLink
    to={item.to}
    end={item.end}
    onClick={onNavigate}
    data-testid={item.testId || `dealer-nav-${item.label.toLowerCase()}`}
    className={({ isActive }) =>
      `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? "bg-brand text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <item.icon size={17} className="shrink-0" />
    <span>{item.label}</span>
  </NavLink>
);

const DealerLayout = () => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const location = useLocation();

  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Fetch billing status once on mount so we can show the setup banner
  useEffect(() => {
    api.get("/dealer/billing/status").then((r) => setBillingStatus(r.data)).catch(() => setBillingStatus(null));
  }, [location.pathname]);

  const initial = (user?.full_name || user?.email || "?").charAt(0).toUpperCase();
  const needsPaymentSetup = billingStatus && billingStatus.payment_setup_status !== "ready";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="dealer-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/dealer" className="flex items-center gap-2">
            <BeloLogo height={28} color="#0f172a" />
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500">
              Dealer
            </span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="dealer-mobile-menu-btn"
          >
            <Menu size={22} />
          </Button>
        </div>
      </div>

      {/* Sidebar (mobile drawer + desktop static) */}
      <aside
        className={`fixed lg:fixed top-0 left-0 z-40 h-screen w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="dealer-sidebar"
      >
        <div className="px-5 h-16 flex items-center justify-between border-b border-slate-100">
          <Link to="/dealer" className="flex items-center gap-2">
            <BeloLogo height={28} color="#0f172a" />
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-500">
              Dealer
            </span>
          </Link>
          <button
            className="lg:hidden text-slate-500"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <NavItem
              key={item.to}
              item={item}
              onNavigate={() => setSidebarOpen(false)}
            />
          ))}
        </nav>
        <div className="border-t border-slate-100 px-3 py-3 space-y-2">
          <div className="px-3 py-2 rounded-lg bg-slate-50">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-brand-light text-brand font-semibold flex items-center justify-center text-sm">
                {initial}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {user?.full_name}
                </p>
                <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
          <Button
            asChild
            variant="ghost"
            className="w-full justify-start text-slate-500 hover:text-slate-900"
            data-testid="dealer-back-to-site"
          >
            <Link to="/">
              <ChevronLeft size={16} /> Back to Belomotors
            </Link>
          </Button>
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            data-testid="dealer-logout-btn"
          >
            <LogOut size={16} /> Log out
          </Button>
        </div>
      </aside>

      {/* Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {needsPaymentSetup && location.pathname !== "/dealer/billing" && (
          <div className="bg-rose-600 text-white px-4 lg:px-8 py-3 flex items-center justify-between gap-3" data-testid="dealer-payment-setup-banner">
            <div className="flex items-center gap-3">
              <AlertCircle size={18} className="shrink-0" />
              <div className="text-sm">
                <span className="font-semibold">Payment method required.</span>{" "}
                Set up billing to receive appointment charges.
              </div>
            </div>
            <Link to="/dealer/billing" className="bg-white text-rose-700 font-semibold text-sm px-4 py-1.5 rounded-lg hover:bg-rose-50 whitespace-nowrap" data-testid="dealer-setup-billing-link">
              Set up billing →
            </Link>
          </div>
        )}
        <main className="min-h-screen" data-testid="dealer-main">
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
};

export default DealerLayout;
